/**
 * Toggle component
 *
 * Show/Hide elements when the trigger element is checked or unchecked
 *
 * @example <caption>Show elements when trigger is checked</caption>
 *
 * <div data-target=".target">...Trigger...</div>
 * <div class="icras-toggle-target target">...Show me when trigger is checked...</div>
 *
 * @example <caption>Hide elements when trigger is checked</caption>
 *
 * <div data-target=".target">...Trigger...</div>
 * <div class="icras-toggle-target target" data-toggle-inverse>...Hide me when trigger is checked...</div>
 */
export default class {
  /**
   * Trigger container
   *
   * @private
   *
   * @type {HTMLElement}
   */
  #container;

  /**
   * Triggering element
   *
   * @private
   *
   * @type {HTMLInputElement}
   */
  #trigger;

  /**
   * Active class name
   *
   * @private
   *
   * @type {string}
   */
  #activeClassName = 'icras-active';

  /**
   * @param {HTMLElement} container Component container
   */
  constructor(container) {
    this.#container = container;
    if (!this.#container) {
      throw new Error('icras Toggle: container is not defined');
    }

    this.#trigger = this.#container.querySelector('input');
    if (!this.#trigger) {
      throw new Error('icras Toggle: trigger is not defined');
    }

    this.#init();
  }

  /**
   * Initialize events and target elements
   *
   * @private
   *
   * @return {void}
   */
  #init() {
    this.#trigger.addEventListener('change', () => this.toggle());
    this.toggle();
  }

  /**
   * Show / hide elements
   *
   * @return {void}
   */
  toggle() {
    const isChecked = this.#trigger.checked;
    document.querySelectorAll(this.#container.dataset.target).forEach((elt) => {
      const show = elt.hasAttribute('data-toggle-inverse')
        ? !isChecked
        : isChecked;

      if (show) {
        elt.classList.add(this.#activeClassName);
      } else {
        elt.classList.remove(this.#activeClassName);
      }
    });
  }
}
